/**
 * DuctHydraulics.js
 * Core duct sizing calculations following ASHRAE/SMACNA methods.
 *
 * ALL calculations are performed internally in imperial units:
 *   flow rate      → CFM (ft³/min)
 *   velocity       → fpm (ft/min)
 *   duct diameter  → inches
 *   duct dimensions→ inches
 *   friction rate  → in. w.c. per 100 ft of duct
 *   altitude       → feet
 *   air density    → lb/ft³ (lbm/ft³)
 *
 * Calculation method: Darcy-Weisbach with Swamee-Jain friction factor.
 *   ε (sheet metal roughness) = 0.0003 ft
 *
 * Unit-system note:
 *   Air density is in lbm/ft³ (not slugs). When inserting lbm density into
 *   the Darcy-Weisbach pressure-drop formula we must divide by g_c = 32.174
 *   (lbm·ft / lbf·s²) to convert the result from lbm/ft²/ft to lbf/ft²/ft:
 *
 *     ΔP/L [lbf/ft²/ft] = f · (ρ[lbm/ft³] · V²[ft²/s²]) / (2 · g_c · D[ft])
 */

// ---------------------------------------------------------------------------
// Air properties
// ---------------------------------------------------------------------------

/**
 * Standard sea-level air density (70 °F, dry air).
 * @type {number} lb/ft³
 */
export const AIR_DENSITY_STD_LB_FT3 = 0.0752;

/**
 * Air kinematic viscosity at ~70 °F, standard pressure.
 * Used for Reynolds number calculation.
 * @type {number} ft²/s
 */
const AIR_KINEMATIC_VISCOSITY_FT2_S = 1.588e-4;

/**
 * Sheet-metal duct absolute roughness.
 * ASHRAE Fundamentals / SMACNA value for galvanised sheet metal.
 * @type {number} ft
 */
const DUCT_ROUGHNESS_FT = 0.0003;

/**
 * Gravitational conversion constant for US Customary unit system.
 * Needed when density is in lbm/ft³ and pressure results must be in lbf/ft².
 * @type {number} lbm·ft / (lbf·s²)
 */
const G_C = 32.174;

// ---------------------------------------------------------------------------
// US Standard Atmosphere pressure model
// ---------------------------------------------------------------------------

/**
 * Returns the ratio P(h) / P_sea-level using the US Standard Atmosphere
 * tropospheric lapse-rate formula (valid from –10,000 ft to ~36,000 ft).
 *
 * P/P₀ = (1 − 6.87535×10⁻⁶ · h_ft)^5.2561
 *
 * @param {number} altitudeFt - Altitude in feet (may be negative for below sea level).
 * @returns {number} pressure ratio (dimensionless)
 */
function pressureRatio(altitudeFt) {
  const inner = 1 - 6.87535e-6 * altitudeFt;
  if (inner <= 0) return 0; // guard for extreme altitudes
  return Math.pow(inner, 5.2561);
}

/**
 * Calculates air density at a given altitude.
 * Assumes constant temperature (density scales linearly with pressure for dry air).
 *
 * @param {number} altitudeFt - Altitude in feet.
 * @returns {number} air density in lb/ft³
 */
export function calcAirDensity(altitudeFt) {
  return AIR_DENSITY_STD_LB_FT3 * pressureRatio(altitudeFt);
}

// ---------------------------------------------------------------------------
// Friction factor (Darcy-Weisbach)
// ---------------------------------------------------------------------------

/**
 * Calculates the Darcy friction factor.
 *
 * Regime:
 *   Re < 2300              → laminar:      f = 64 / Re
 *   Re ≥ 4000              → turbulent:    Swamee-Jain explicit approximation
 *   2300 ≤ Re < 4000       → transitional: linear blend
 *
 * Swamee-Jain (1976):
 *   f = 0.25 / [ log10( ε/(3.7·D) + 5.74/Re^0.9 ) ]²
 *
 * @param {number} Re       - Reynolds number
 * @param {number} D_ft     - Hydraulic diameter in feet
 * @returns {number} Darcy friction factor
 */
function darcyFrictionFactor(Re, D_ft) {
  if (Re <= 0) return 0;
  const eps = DUCT_ROUGHNESS_FT;

  const fLaminar = () => 64 / Re;
  const fTurbulent = () => {
    const inner = eps / (3.7 * D_ft) + 5.74 / Math.pow(Re, 0.9);
    return 0.25 / Math.pow(Math.log10(inner), 2);
  };

  if (Re < 2300) return fLaminar();
  if (Re >= 4000) return fTurbulent();
  // Transitional blend
  const t = (Re - 2300) / (4000 - 2300);
  return fLaminar() * (1 - t) + fTurbulent() * t;
}

// ---------------------------------------------------------------------------
// Conversion helpers
// ---------------------------------------------------------------------------

/** Convert inches to feet. */
const inToFt = (in_) => in_ / 12;

/** Convert feet to inches. */
const ftToIn = (ft) => ft * 12;

/** Convert fpm to fps. */
const fpmToFps = (fpm) => fpm / 60;

/**
 * Convert friction rate in in. w.c. / 100 ft → lbf/ft² per ft (psf/ft).
 * 1 in. w.c. = 5.1925 lbf/ft²
 */
const inWcPer100FtToPsfPerFt = (val) => val * 5.1925 / 100;

/**
 * Convert lbf/ft² per ft → in. w.c. per 100 ft.
 */
const psfPerFtToInWcPer100Ft = (val) => val * 100 / 5.1925;

// ---------------------------------------------------------------------------
// Core calculation: friction rate for a round duct
// ---------------------------------------------------------------------------

/**
 * Computes the friction rate (in. w.c. / 100 ft) for a given flow, diameter,
 * and air density.
 *
 * Using Darcy-Weisbach:
 *   ΔP/L = f · (ρ · V²) / (2 · D_h)     [lbf/ft² per ft]
 *
 * @param {number} Q_cfm     - Flow rate in CFM (> 0)
 * @param {number} D_in      - Round duct diameter in inches (> 0)
 * @param {number} rho       - Air density in lb/ft³
 * @returns {number} friction rate in in. w.c. per 100 ft
 */
export function calcFrictionRate(Q_cfm, D_in, rho) {
  if (Q_cfm <= 0 || D_in <= 0) return 0;

  const A_ft2 = Math.PI * Math.pow(inToFt(D_in) / 2, 2);
  const V_fps = fpmToFps(Q_cfm / A_ft2); // ft/s
  const D_ft = inToFt(D_in);

  const Re = (V_fps * D_ft) / AIR_KINEMATIC_VISCOSITY_FT2_S;
  const f = darcyFrictionFactor(Re, D_ft);

  // ΔP/L in lbf/ft² per ft — divide by G_C because ρ is in lbm/ft³, not slugs/ft³.
  // ΔP/L [lbf/ft²/ft] = f · ρ[lbm/ft³] · V²[ft²/s²] / (2 · g_c · D_h[ft])
  const dPdL = f * (rho * V_fps * V_fps) / (2 * G_C * D_ft);
  return psfPerFtToInWcPer100Ft(dPdL);
}

/**
 * Computes velocity (fpm) for a given flow and round duct diameter.
 *
 * @param {number} Q_cfm - Flow rate in CFM
 * @param {number} D_in  - Round duct diameter in inches
 * @returns {number} velocity in fpm
 */
export function calcRoundDuctVelocity(Q_cfm, D_in) {
  if (D_in <= 0) return 0;
  const A_ft2 = Math.PI * Math.pow(inToFt(D_in) / 2, 2);
  return Q_cfm / A_ft2; // fpm
}

// ---------------------------------------------------------------------------
// Sizing: round duct diameter
// ---------------------------------------------------------------------------

/**
 * Calculates the round duct diameter required to carry Q_cfm at a given velocity.
 *
 * D = sqrt(4 * Q / (π * V))   (all in ft), result converted to inches.
 *
 * @param {number} Q_cfm   - Flow rate in CFM (> 0)
 * @param {number} V_fpm   - Design velocity in fpm (> 0)
 * @returns {number} round duct diameter in inches
 */
export function sizeDuctFromVelocity(Q_cfm, V_fpm) {
  if (Q_cfm <= 0 || V_fpm <= 0) return 0;
  const A_ft2 = Q_cfm / V_fpm;
  const D_ft = Math.sqrt((4 * A_ft2) / Math.PI);
  return ftToIn(D_ft);
}

/**
 * Calculates the round duct diameter required to achieve a given friction rate,
 * using bisection search across the Darcy-Weisbach equation.
 *
 * @param {number} Q_cfm   - Flow rate in CFM (> 0)
 * @param {number} FR      - Target friction rate in in. w.c. per 100 ft (> 0)
 * @param {number} rho     - Air density in lb/ft³
 * @returns {number} round duct diameter in inches
 */
export function sizeDuctFromFrictionRate(Q_cfm, FR, rho) {
  if (Q_cfm <= 0 || FR <= 0 || rho <= 0) return 0;

  // Friction rate decreases as D increases.
  // Bisect on D in the range [1, 200] inches.
  let D_low = 1;
  let D_high = 200;

  // Quick sanity: if friction rate at D_high is still > target, the flow is enormous
  if (calcFrictionRate(Q_cfm, D_high, rho) > FR) {
    return D_high;
  }
  // If friction rate at D_low is < target, the flow is tiny
  if (calcFrictionRate(Q_cfm, D_low, rho) < FR) {
    return D_low;
  }

  for (let i = 0; i < 100; i++) {
    const D_mid = (D_low + D_high) / 2;
    const fr_mid = calcFrictionRate(Q_cfm, D_mid, rho);
    if (fr_mid > FR) {
      // Too much friction → need bigger duct
      D_low = D_mid;
    } else {
      D_high = D_mid;
    }
    if (D_high - D_low < 0.0001) break;
  }

  return (D_low + D_high) / 2;
}

// ---------------------------------------------------------------------------
// Rectangular duct equivalence
// ---------------------------------------------------------------------------

/**
 * Returns the hydraulic diameter of a rectangular duct (all in same units).
 *   D_h = 4 · A / P = 2·W·H / (W + H)
 *
 * @param {number} W - Width
 * @param {number} H - Height (same units as W)
 * @returns {number} hydraulic diameter (same units)
 */
export function rectangularHydraulicDiameter(W, H) {
  if (W <= 0 || H <= 0) return 0;
  return (2 * W * H) / (W + H);
}

/**
 * Returns the ASHRAE equal-friction equivalent diameter for a rectangular duct.
 *
 * D_e = 1.3 · (a·b)^0.625 / (a + b)^0.25
 *
 * This gives the round diameter that has the same friction rate per unit length
 * as the rectangular duct at the same flow rate.
 *
 * @param {number} a - One side in inches
 * @param {number} b - Other side in inches
 * @returns {number} equivalent round diameter in inches
 */
export function ashraEqualFrictionDiameter(a, b) {
  if (a <= 0 || b <= 0) return 0;
  return 1.3 * Math.pow(a * b, 0.625) / Math.pow(a + b, 0.25);
}

/**
 * Given a target equivalent round diameter and one fixed dimension of a
 * rectangular duct, computes the other dimension using the ASHRAE equal-
 * friction equation.
 *
 * D_e = 1.3 · (a·b)^0.625 / (a + b)^0.25  →  solve for b given a and D_e
 *
 * Uses bisection because the equation is not easily invertible analytically.
 *
 * @param {number} D_e_in  - Target equivalent round diameter in inches
 * @param {number} a_in    - Fixed dimension in inches
 * @returns {number} computed dimension b in inches
 */
export function rectDimFromEquivDiameter(D_e_in, a_in) {
  if (D_e_in <= 0 || a_in <= 0) return 0;

  // b grows as D_e grows (for fixed a). Bisect.
  let b_low = 0.01;
  let b_high = 500; // practical upper bound in inches

  for (let i = 0; i < 200; i++) {
    const b_mid = (b_low + b_high) / 2;
    const De = ashraEqualFrictionDiameter(a_in, b_mid);
    if (De < D_e_in) {
      b_low = b_mid;
    } else {
      b_high = b_mid;
    }
    if (b_high - b_low < 0.001) break;
  }

  return (b_low + b_high) / 2;
}

/**
 * Computes velocity (fpm) for a rectangular duct.
 *
 * @param {number} Q_cfm - Flow rate in CFM
 * @param {number} W_in  - Width in inches
 * @param {number} H_in  - Height in inches
 * @returns {number} velocity in fpm
 */
export function calcRectVelocity(Q_cfm, W_in, H_in) {
  const A_ft2 = (W_in * H_in) / 144;
  if (A_ft2 <= 0) return 0;
  return Q_cfm / A_ft2;
}

/**
 * Computes friction rate (in. w.c. / 100 ft) for a rectangular duct.
 * Uses the equal-friction equivalent diameter approach (ASHRAE).
 *
 * @param {number} Q_cfm - Flow rate in CFM
 * @param {number} W_in  - Width in inches
 * @param {number} H_in  - Height in inches
 * @param {number} rho   - Air density in lb/ft³
 * @returns {number} friction rate in in. w.c. per 100 ft
 */
export function calcRectFrictionRate(Q_cfm, W_in, H_in, rho) {
  const D_e = ashraEqualFrictionDiameter(W_in, H_in);
  return calcFrictionRate(Q_cfm, D_e, rho);
}

// ---------------------------------------------------------------------------
// Standard duct size rounding
// ---------------------------------------------------------------------------

/**
 * Standard round duct sizes (inches).
 * Increments of 2 inches, starting from 4, with 5 as an exception.
 */
export const STANDARD_ROUND_SIZES_IN = [
  4, 5, 6, 8, 10, 12, 14, 16, 18, 20, 22, 24, 26, 28, 30,
  32, 34, 36, 38, 40, 42, 44, 46, 48, 50, 52, 54, 56, 58, 60,
];

/**
 * Standard rectangular duct increments (inches): every 2 inches.
 * Used for individual dimension rounding.
 */
const RECT_INCREMENT_IN = 2;

/**
 * Round a duct dimension to the nearest standard increment (2 in).
 * Minimum is 4 inches.
 *
 * @param {number} val_in - Value in inches
 * @returns {number} rounded value in inches
 */
export function roundRectDimension(val_in) {
  const rounded = Math.round(val_in / RECT_INCREMENT_IN) * RECT_INCREMENT_IN;
  return Math.max(4, rounded);
}

/**
 * Round up a duct dimension to the next standard increment (2 in).
 * Minimum is 4 inches.
 *
 * @param {number} val_in - Value in inches
 * @returns {number} rounded-up value in inches
 */
export function roundUpRectDimension(val_in) {
  const next = Math.ceil(val_in / RECT_INCREMENT_IN) * RECT_INCREMENT_IN;
  return Math.max(4, next);
}

/**
 * Round a round-duct diameter to the nearest standard size.
 *
 * @param {number} D_in - Diameter in inches
 * @returns {number} nearest standard diameter in inches
 */
export function roundRoundDiameter(D_in) {
  let best = STANDARD_ROUND_SIZES_IN[0];
  let bestDiff = Infinity;
  for (const s of STANDARD_ROUND_SIZES_IN) {
    const diff = Math.abs(s - D_in);
    if (diff < bestDiff) {
      bestDiff = diff;
      best = s;
    }
  }
  return best;
}

/**
 * Round up a round-duct diameter to the next standard size.
 *
 * @param {number} D_in - Diameter in inches
 * @returns {number} next-up standard diameter in inches
 */
export function roundUpRoundDiameter(D_in) {
  for (const s of STANDARD_ROUND_SIZES_IN) {
    if (s >= D_in - 1e-9) return s;
  }
  return STANDARD_ROUND_SIZES_IN[STANDARD_ROUND_SIZES_IN.length - 1];
}

// ---------------------------------------------------------------------------
// Aspect ratio
// ---------------------------------------------------------------------------

/**
 * Returns the aspect ratio for a rectangular duct (always ≥ 1).
 *
 * @param {number} W - Width
 * @param {number} H - Height (same units)
 * @returns {number} aspect ratio
 */
export function aspectRatio(W, H) {
  if (W <= 0 || H <= 0) return 0;
  return Math.max(W, H) / Math.min(W, H);
}

// ---------------------------------------------------------------------------
// Full "Size Duct" calculation
// ---------------------------------------------------------------------------

/**
 * Sizes a duct given flow rate and either velocity or friction rate input.
 *
 * @param {object} params
 * @param {number} params.Q_cfm       - Flow rate in CFM
 * @param {'velocity'|'frictionRate'} params.sizingMethod
 * @param {number} [params.V_fpm]     - Design velocity in fpm (if method='velocity')
 * @param {number} [params.FR]        - Target friction rate in in.wc/100ft (if method='frictionRate')
 * @param {number} params.rho         - Air density in lb/ft³
 * @param {number} [params.rectWidth] - Fixed rectangular width in inches (optional)
 * @returns {object} results
 */
export function sizeDuct({ Q_cfm, sizingMethod, V_fpm, FR, rho, rectWidth }) {
  let roundD_in;
  if (sizingMethod === 'velocity') {
    roundD_in = sizeDuctFromVelocity(Q_cfm, V_fpm);
  } else {
    roundD_in = sizeDuctFromFrictionRate(Q_cfm, FR, rho);
  }

  // Derived values for round duct
  const roundV_fpm = calcRoundDuctVelocity(Q_cfm, roundD_in);
  const roundFR = calcFrictionRate(Q_cfm, roundD_in, rho);

  // Rectangular equivalent: use rectWidth as the fixed "a" dimension
  // Default: target aspect ratio ≤ 3:1, so start with a = round(D/2) → approx square
  const fixedWidth_in = (rectWidth > 0) ? rectWidth : Math.max(4, roundRectDimension(roundD_in * 0.6));
  const rectHeight_in = rectDimFromEquivDiameter(roundD_in, fixedWidth_in);
  const rectAR = aspectRatio(fixedWidth_in, rectHeight_in);

  return {
    roundD_in,
    roundV_fpm,
    roundFR,
    rectWidth_in: fixedWidth_in,
    rectHeight_in,
    rectAR,
  };
}

// ---------------------------------------------------------------------------
// Full "Analyze Duct" calculation
// ---------------------------------------------------------------------------

/**
 * Analyzes an existing duct (given geometry, returns performance values).
 *
 * @param {object} params
 * @param {'round'|'rectangular'} params.ductShape
 * @param {number} [params.roundD_in]   - Round duct diameter in inches
 * @param {number} [params.rectW_in]    - Rectangular duct width in inches
 * @param {number} [params.rectH_in]    - Rectangular duct height in inches
 * @param {number} params.Q_cfm         - Flow rate in CFM
 * @param {number} params.rho           - Air density in lb/ft³
 * @returns {object} analysis results
 */
export function analyzeDuct({ ductShape, roundD_in, rectW_in, rectH_in, Q_cfm, rho }) {
  if (ductShape === 'round') {
    const V_fpm = calcRoundDuctVelocity(Q_cfm, roundD_in);
    const FR = calcFrictionRate(Q_cfm, roundD_in, rho);
    // Provide rectangular equivalent
    const fixedW = Math.max(4, roundRectDimension(roundD_in * 0.6));
    const eqH = rectDimFromEquivDiameter(roundD_in, fixedW);
    return {
      V_fpm,
      FR,
      equivRectW_in: fixedW,
      equivRectH_in: eqH,
      equivRectAR: aspectRatio(fixedW, eqH),
      equivRoundD_in: null,
    };
  } else {
    // Rectangular input
    const V_fpm = calcRectVelocity(Q_cfm, rectW_in, rectH_in);
    const FR = calcRectFrictionRate(Q_cfm, rectW_in, rectH_in, rho);
    const equivD = ashraEqualFrictionDiameter(rectW_in, rectH_in);
    return {
      V_fpm,
      FR,
      equivRoundD_in: equivD,
      equivRectW_in: null,
      equivRectH_in: null,
      equivRectAR: null,
    };
  }
}
